/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.
*/

import { CopyButton, useTranslationContext } from "@gnu-taler/web-util/browser";
import { ComponentChildren, VNode, h } from "preact";

export function ReadOnlyDetails({
  children,
}: {
  children: ComponentChildren;
}): VNode {
  return (
    <div class="overflow-hidden bg-white shadow-sm ring-1 ring-onBackground/5 sm:rounded-xl md:col-span-2">
      <dl class="divide-y divide-onBackground/10">{children}</dl>
    </div>
  );
}

export function ReadOnlyDetail({
  label,
  value,
  copyValue,
  description,
}: {
  label: string;
  value: ComponentChildren;
  copyValue?: string;
  description?: ComponentChildren;
}): VNode {
  const { i18n } = useTranslationContext();
  return (
    <div class="px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-8">
      <dt class="text-sm font-medium text-onBackground">{label}</dt>
      <dd class="mt-1 min-w-0 text-sm text-secondary sm:col-span-2 sm:mt-0">
        <div class="flex min-w-0 items-start justify-between gap-3">
          <span class="min-w-0 break-all text-onBackground">{value}</span>
          {copyValue === undefined ? undefined : (
            <CopyButton
              class="inline-flex shrink-0 items-center gap-1 rounded-md p-2 text-secondary hover:bg-primary/10 hover:text-primaryDark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              getContent={() => copyValue}
            >
              <span class="sr-only">
                <i18n.Translate>Copy</i18n.Translate>
              </span>
            </CopyButton>
          )}
        </div>
        {description === undefined ? undefined : (
          <p class="mt-1 text-gray-500">{description}</p>
        )}
      </dd>
    </div>
  );
}
